/**
 * Showing customer- or staff-written email HTML safely (ADR-0026, ADR-0039).
 *
 * `sandbox=""` gives the frame a unique opaque origin with scripts, forms,
 * popups and top navigation disabled, so template HTML can never read the
 * app's session, call the API or redirect the page. A strict CSP inside the
 * document additionally blocks remote loads (tracking pixels, fonts).
 */

const PREVIEW_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'";

export function previewDocument(html: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${PREVIEW_CSP}"><base target="_blank"></head><body>${html}</body></html>`;
}

// --- Sample values for pictures of templates -------------------------------------------
// Template source made to read like a real email: `{{ variables }}` become sample
// values and `{% tags %}` disappear. A picture, not a renderer.

const SAMPLES: Record<string, string> = {
  name: "Jane Doe",
  full_name: "Jane Doe",
  first_name: "Jane",
  last_name: "Doe",
  email: "jane@example.com",
  company: "Acme",
  organization: "Acme",
  city: "Mumbai",
  plan: "Pro",
  item: "Item",
  organization_address: "Your postal address",
};

const STRING_LITERAL = /default\(\s*(["'])(.*?)\1/;

function words(variable: string): string {
  return variable
    .split(/[._]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** A sample value for one `{{ ... }}` expression. */
export function sampleValue(expression: string): string {
  const fallback = STRING_LITERAL.exec(expression);
  if (fallback?.[2] != null) return fallback[2];
  const variable = expression.split("|")[0]?.trim() ?? "";
  const last = variable.split(".").at(-1) ?? variable;
  const known = SAMPLES[variable] ?? SAMPLES[last];
  if (known) return known;
  if (/(^|_)(url|link)$/.test(last)) return "#";
  return words(variable) || "…";
}

export function sampleFill(source: string): string {
  return source
    // Show only the first branch of an if/else, then drop the remaining tags.
    .replace(/\{%-?\s*else\s*-?%\}[\s\S]*?\{%-?\s*endif\s*-?%\}/g, "")
    .replace(/\{%[\s\S]*?%\}/g, "")
    .replace(/\{#[\s\S]*?#\}/g, "")
    .replace(/\{\{([\s\S]*?)\}\}/g, (_, expression: string) => sampleValue(expression));
}
